import { useCallback, useContext, useEffect, useState } from 'react';

import { useLazyGetClientsMessagesQuery } from '@app/api/Clients/api';
import { Error403Unauthorized } from '@app/components';
import {
  DATE_PICKER_FORMAT,
  FRONT_WARNING,
  ISO_FORMAT_INDEX,
  MOBILE,
  PREFIX,
  RIGHTS_IS_ON,
  RIGHTS_LEADS,
  today,
  WARNING,
} from '@app/constants';
import { useAccessRights, useMemoizeObject, useUserPrint } from '@app/hooks';
import { MessagesPageHeader } from '@app/pages/Messages/MessagesList/components/MessagesPageHeader';
import { MessagesTable } from '@app/pages/Messages/MessagesList/components/MessagesTable';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import type { NMessage } from '@app/types';
import type { NSmallGlobalEntities } from '@app/types/SmallGlobalEntities';
import { changeStandardPaginationUi } from '@app/utils';

export const Messages = () => {
  const { showNotification } = useContext(NotificationContext);

  const { activeBranchId } = useUserPrint();
  const { hasUserRightForModuleView: hasUserRightToViewLeads } =
    useAccessRights(RIGHTS_LEADS, RIGHTS_IS_ON);
  const [getClientsMessages] = useLazyGetClientsMessagesQuery();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [tableLoader, setTableLoader] = useState<boolean>(false);
  const [messages, setMessages] = useState<NMessage.ModelClientMessage[]>([]);
  const [datesRange, setDatesRange] = useState<NSmallGlobalEntities.RangeValue>(
    [today, today]
  );

  const memoizedDatesRange = useMemoizeObject(datesRange);

  const fetchClientsMessages = useCallback(async () => {
    try {
      setTableLoader(true);

      if (!memoizedDatesRange?.[0] || !memoizedDatesRange?.[1])
        return showNotification(
          'Интервал дат для получения сообщений не определен',
          FRONT_WARNING
        );

      const result = await getClientsMessages({
        ActiveBranchId: activeBranchId,
        dateS: memoizedDatesRange?.[0]?.format(
          DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]
        ),
        dateE: memoizedDatesRange?.[1]?.format(
          DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]
        ),
      }).unwrap();

      setCurrentPage(1);
      setMessages(result);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    } finally {
      setTableLoader(false);
    }
  }, [
    activeBranchId,
    memoizedDatesRange,
    showNotification,
    getClientsMessages,
  ]);

  useEffect(() => {
    fetchClientsMessages();
    // fetchClientsMessages в зависимостях вызывает лишние обращения к серверу
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMobile = innerWidth <= MOBILE;

  useEffect(() => {
    changeStandardPaginationUi({
      isMobile,
    });
  }, [messages, isMobile]);

  if (!hasUserRightToViewLeads) {
    return <Error403Unauthorized />;
  }

  return (
    <div className={`${PREFIX}messages`}>
      <MessagesPageHeader
        fetchClientsMessages={fetchClientsMessages}
        datesRange={datesRange}
        setDatesRange={setDatesRange}
      />
      <MessagesTable
        isMobile={isMobile}
        tableLoader={tableLoader}
        messages={messages}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  );
};
